<script setup lang="ts">
    import { defaultAutocompleteConverter } from "@/components/forms/inputs/Autocomplete/helpers";
    import KAutocomplete from "@/components/forms/inputs/Autocomplete/KAutocomplete.vue";
    import { Option } from "@/components/forms/inputs/Autocomplete/types";
    import { fetchProfilesRollAndOrgUnit } from "@/components/forms/reusables/profileHelper";
    import { defineEmits, defineProps, onMounted, ref, toRefs, withDefaults } from "vue";

    interface Props {
        name: string;
        urlParams?: { [key: string]: unknown };
        showRoll?: "yes" | "if-multiple" | "no";
        /**
         * Ska options hämtas direkt vid mount?
         */
        retrieveOptionsOnMount?: boolean;
        showOrgUnit?: "yes" | "if-multiple" | "no";
        onlyActive?: boolean;
        onlyPermitted?: boolean;
        /**
         * Ska det gå att söka mot BE?
         */
        allowSearch?: boolean;
        label?: string;
        ignoreEmptyData?: boolean;
    }

    const props = withDefaults(defineProps<Props>(), {
        retrieveOptionsOnMount: true,
        showRoll: "no",
        showOrgUnit: "if-multiple",
        onlyActive: true,
        onlyPermitted: true,
        allowSearch: false,
    });

    const emit = defineEmits<{
        (e: "option:selected", value: unknown): void;
    }>();

    const { urlParams, retrieveOptionsOnMount, showRoll, showOrgUnit, onlyActive, onlyPermitted } = toRefs(props);

    const profileAutocomplete = ref();
    const options = ref<Option[]>([]);

    onMounted(async () => {
        if (retrieveOptionsOnMount.value) {
            const params = {
                orgUnit__only_permitted: onlyPermitted.value,
                isActive: onlyActive.value ? true : undefined,
                ...urlParams?.value,
            };
            options.value = await fetchProfilesRollAndOrgUnit({
                params,
                showRoll: showRoll.value,
                showOrgUnit: showOrgUnit.value,
            });
            profileAutocomplete?.value?.setOptions?.({ options: options.value, setDefault: true });
        }
    });

    const findOption = (option: Option) => {
        return options.value?.find((o: Option) => o.value === option.value)?.label ?? option.label;
    };

    const onSearch = async ({ query }: { query?: string }) => {
        return fetchProfilesRollAndOrgUnit({
            params: {
                orgUnit__only_permitted: onlyPermitted.value,
                isActive: onlyActive.value ? true : undefined,
                q: query,
                ...urlParams?.value,
            },
            showOrgUnit: showOrgUnit.value,
            showRoll: showRoll.value,
        });
    };

    defineExpose({
        setOptions: ({ options, setDefault = true }: { options: Option[]; setDefault?: boolean }) => {
            profileAutocomplete?.value?.setOptions?.({ options, setDefault });
        },
    });
</script>

<template>
    <KAutocomplete
        v-bind="$attrs"
        ref="profileAutocomplete"
        :name="name"
        :options="[]"
        :converter="defaultAutocompleteConverter"
        :onSearch="allowSearch ? onSearch : undefined"
        :label="label"
        :ignoreEmptyData="ignoreEmptyData"
        @option:selected="emit('option:selected', $event)"
    >
        <template #selected-option="{ option }">
            <span>{{ findOption(option) }}</span>
        </template>
    </KAutocomplete>
</template>

<style lang="scss" scoped></style>
